import {
  Box,
  Flex,
  Grid,
  GridItem,
  Icon,
  Image,
  Text,
  chakra,
  shouldForwardProp,
  useBreakpointValue,
} from "@chakra-ui/react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import InfiniteScroll from "react-infinite-scroller";
import { useSelector } from "react-redux";
import { Link, useParams, useSearchParams } from "react-router-dom";
import NotFound from "../../ProgressBlock/NotFound";
import LoadingBlock from "../../Loading/LoadingBlock";
import useDeviceType from "../../../hook/useDeviceType";
import { languageTransfer } from "../../../utils/languageTransfer";
import i18next from "i18next";
import { routeMapping } from "../GameTypeSelector/utils/routeMapping";
import { useTranslation } from "react-i18next";
import SubListSelector from "../SubListSelector/SubListSelector";
import TitleIcon from "../PlatformSelector/components/titleIcon";
import { menuIcon } from "../../Menu/helper/menuIcon";
import { isValidMotionProp, motion } from "framer-motion";
import { gameListFromBottom } from "../../../animateConstant";
import { menuIconV2 } from "../../Menu/helper/menuIcon_v2";
import GameItem from "../../GameItem/GameItem";
import { COMMON_WEB_PATH } from "../../../constant";
import useCheckLogin from "../../../hook/useCheckLogin";
import useWebsiteInfo from "../../../hook/useWebsiteInfo";
import VerticalGameSwitcher from "./VerticalGameSwitcher";
import useIsLandScapeMachine from "../../../hook/useIsLandScapeMachine";
import { RiMenuSearchFill } from "react-icons/ri";
import CasinoGroupLink from "../../../pages/CasinoGroups/CasinoGroupLink";

const ChakraBox = chakra(motion.div, {
  shouldForwardProp: (prop) =>
    isValidMotionProp(prop) || shouldForwardProp(prop),
});

const GameSelector = ({
  subListSelector,
  hiddenTitle,
  gameMainTypeFromProps,
  customSubType,
  customGamePlatform,
  sliceCount,
  isNew,
  isHot,
  isHighlight,
  ExclusiveLobbyStyle, //首頁大廳獨家遊戲樣式
  onGameHover, // 遊戲hover
  hiddenGameSearch,
}) => {
  const { t } = useTranslation();
  const i18n = (key) => t(`common.${key}`);
  const { getDynamicImagePath } = useWebsiteInfo();

  const scrollRef = useRef(null);
  const [highlightUid, setHighlightUid] = useState(null);

  const [displayedItems, setDisplayedItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  const { gameMainType, gamePlatform, subType } = useParams();
  const { isMachineUser } = useCheckLogin();
  const isMachineLandscape = useIsLandScapeMachine();

  const [searchParams, setSearchParams] = useSearchParams();

  const showVertical = searchParams.get("show_vertical");
  const custom_subType = useMemo(() => {
    return customSubType || subType;
  }, [customSubType, subType]);

  const custom_gameplatform = useMemo(() => {
    return customGamePlatform || gamePlatform;
  }, [customGamePlatform, gamePlatform]);
  const isMobile = useDeviceType() === "Mobile";

  const isHandleAllGame = useSelector((state) => state.isHandleAllGame);
  const {
    sortedByPlatformGames,
    sortedByPlatformAndCategoryGames,
    gameList,
    gameListByTypeAndSubType,
    allGames,
    favoriteGameList,
    randomlySelectedGames,
    newGameList,
    topGameList,
    allGameType,
    exclusiveBrandGames,
    highlightGames,
  } = isHandleAllGame || {};

  const platformGamesList = useMemo(() => {
    const mainType = gameMainTypeFromProps || gameMainType;
    const current_game_type = routeMapping(mainType)?.origin;

    const gameTypeCode = allGameType?.find(
      (t) => t.gameTypeName === current_game_type,
    )?.gameTypeUid;

    // 特別處理customSubType="topGameList"的情況 顯示全部topGameList
    if (customSubType === "topGameList") {
      return topGameList || []; // 直接返回全部topGameList
    }

    // 特別處理customSubType="New" 的情況
    if (customSubType === "New") {
      return newGameList || []; // 直接返回 newGameList
    }

    // 特別處理customSubType="exclusiveBrandGames"的情況 顯示全部獨家遊戲
    if (customSubType === "exclusiveBrandGames") {
      return exclusiveBrandGames || []; // 直接返回全部獨家遊戲
    }

    // 特別處理customSubType="Popular"的情況 從highlightGames取前40個
    if (customSubType === "Popular") {
      const gamesArray = Object.values(highlightGames || {});

      // 根據 game_tags 排序
      const sortedGames = gamesArray.sort((a, b) => {
        const aHotTag = a.game_tags?.find((tag) => tag.startsWith("hot_"));
        const bHotTag = b.game_tags?.find((tag) => tag.startsWith("hot_"));

        // 如果沒有 hot tag，排到最後面
        if (!aHotTag) return 1;
        if (!bHotTag) return -1;

        const aOrder = parseInt(aHotTag.split("_")[1]);
        const bOrder = parseInt(bHotTag.split("_")[1]);

        return aOrder - bOrder;
      });

      // 過濾掉沒有 hot tag 的遊戲並返回前40個
      return sortedGames.slice(0, 40);
    }

    if (custom_subType) {
      const determineIsAllPlatform = (gameList) => {
        if (custom_gameplatform === "all-platform") {
          return (
            gameList?.filter((game) => game.gameTypeUid === gameTypeCode) || []
          );
        } else {
          return (
            gameList
              ?.filter((game) => game.gameTypeUid === gameTypeCode)
              ?.filter(
                (game) =>
                  game.game_platform === custom_gameplatform ||
                  game.SubPlatformEnum === custom_gameplatform,
              ) || []
          );
        }
      };
      switch (custom_subType) {
        case "AllGames":
          return determineIsAllPlatform(allGames);
        case "MyFavorite":
          return determineIsAllPlatform(favoriteGameList);
        case "ClickedGames":
          return determineIsAllPlatform(randomlySelectedGames);
        case "News":
          return determineIsAllPlatform(newGameList);
        case "Top":
          return determineIsAllPlatform(topGameList);
        default:
          if (custom_gameplatform === "all-platform") {
            return (
              gameListByTypeAndSubType?.[current_game_type]?.[
                custom_subType
              ]?.filter((game) => game.gameTypeUid === gameTypeCode) || []
            );
          } else {
            return (
              sortedByPlatformAndCategoryGames?.[custom_gameplatform]?.[
                custom_subType
              ] || []
            );
          }
      }
    }
    if (gameList && mainType && custom_gameplatform) {
      return gameList?.[current_game_type]?.[custom_gameplatform] || [];
    } else {
      return Object?.values(gameListByTypeAndSubType?.[current_game_type] || [])
        ?.flat()
        ?.filter((game) => game.gameTypeUid === gameTypeCode)
        ?.slice(0, 150);
    }
  }, [
    gameList,
    custom_gameplatform,
    gameMainType,
    custom_subType,
    sortedByPlatformAndCategoryGames,
    gameListByTypeAndSubType,
    gameMainTypeFromProps,
    allGameType,
    highlightGames,
  ]);

  const ITEMS_PER_PAGE = useBreakpointValue({
    base: 12,
    md: 25,
    lg: 35,
    xl: 32,
  });

  useEffect(() => {
    if (isLoading) {
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    }
  }, [isLoading]);

  useEffect(() => {
    setCurrentPage(1);
    // setIsLoading(true);
    setDisplayedItems(platformGamesList?.slice(0, ITEMS_PER_PAGE));
  }, [platformGamesList, custom_gameplatform, subType, gameMainType]);

  const gameListSlice = useMemo(() => {
    let list = platformGamesList;
    if (isMachineLandscape) {
      return list
        .filter((item) => {
          if (showVertical === "1") {
            return item.support_vertical === 1;
          } else {
            return item;
          }
        })
        ?.sort((a, b) => b.gameHighlight - a.gameHighlight)
        .sort((a, b) => {
          return b.support_vertical - a.support_vertical;
        });
    } else {
      if (sliceCount) {
        return list
          ?.sort((a, b) => b.gameHighlight - a.gameHighlight)
          ?.slice(0, sliceCount);
      } else {
        return list?.sort((a, b) => b.gameHighlight - a.gameHighlight);
      }
    }
  }, [sliceCount, platformGamesList, isMachineLandscape, showVertical]);

  useEffect(() => {
    const savedUid = sessionStorage.getItem("lastClickedGameUid");

    if (gameListSlice?.length > 0) {
      if (savedUid) {
        setHighlightUid(savedUid);

        const timer = setTimeout(() => {
          const targetElement = document.getElementById(
            `game-item-${savedUid}`,
          );
          if (targetElement) {
            targetElement.scrollIntoView({
              behavior: "auto",
              block: "center",
            });
          }
          sessionStorage.removeItem("lastClickedGameUid");
        }, 300);

        return () => {
          clearTimeout(timer);
        };
      }
    }
  }, [gameListSlice]);
  return (
    <Box
      ref={scrollRef}
      css={{
        "&::-webkit-scrollbar": { display: "none" },
        scrollbarWidth: "none",
      }}
      // borderTopRadius={`common`}
      // borderTop="3px solid"
      // borderColor={`brand.400`}
      // bgGrdi="linear(to-b, transparent, dark.500 400%)"
      // px="common-y"
      minH={{
        // base: "100vh",
        md: "unset",
      }}
      px={{
        base: "0px",
        md: "15px",
      }}
      py={{
        base: "0px",
        md: "px",
      }}
      my={"5px"}
      position="relative"
      bgPosition="center"
      bgRepeat="no-repeat"
      //bgSize={gameListSlice?.length > 10 ? "cover" : "100% 100%"}
      bgSize={{
        base: "cover",
        sm: gameListSlice?.length > 10 ? "cover" : "100% 100%",
      }}
      borderRadius={"15px"}
    >
      {hiddenTitle ? null : (
        <>
          <Flex w="100%" alignItems="end" justifyContent="space-between">
            <Box
              position="relative"
              w="100%"
              maxW="60%"
              display={{
                base: "none",
                xl: "block",
              }}
            >
              <Box
                bgSize="110%150%"
                bgPos="center"
                h={"200px"}
                bgImage={`url(${getDynamicImagePath(
                  `${gameMainTypeFromProps}gameTitle`,
                )})`}
              ></Box>
            </Box>
          </Flex>
          <Box
            mb="10px"
            display={{
              base: "block",
              xl: "none",
            }}
          >
            <Flex alignItems={`center`} gap="5px">
              {/* <TitleIcon
              icon={menuIconV2(gameMainTypeFromProps || gameMainType)?.icon}
            /> */}
              <Image
                src={`${COMMON_WEB_PATH}/v4-layout/game-type-icon/${menuIconV2(
                  gameMainTypeFromProps || gameMainType,
                )?.text?.toUpperCase()}.svg`}
                w="27px"
                h="27px"
              />

              <Box
                display="flex"
                alignItems={`center`}
                sx={{
                  "& > p": {
                    // 應用到所有子Text元素
                    background:
                      "linear-gradient(180deg, rgba(2, 0, 36, 1) 0%, rgb(207 100 255) 0%, rgb(255 255 255) 50%, rgb(228 184 255) 75%, rgb(210 156 237) 100%)",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    color: "transparent",
                    textShadow: "0px 0px 3px rgb(158 0 255 / 30%)",
                  },
                }}
              >
                <Text
                  fontWeight={`700`}
                  fontSize={{
                    base: "20px",
                    md: "20px",
                  }}
                  display="flex"
                  alignItems={`center`}
                >
                  {i18n(gameMainTypeFromProps || gameMainType)}
                  {/* {subType ? `｜${i18n(subType)}` : `｜${i18n("all-platform")}`}
            {subType ? `｜${i18n(subType)}` : ""} */}
                </Text>
                <Text
                  fontWeight={`700`}
                  fontSize={{
                    base: "20px",
                    md: "20px",
                  }}
                  display="flex"
                  alignItems={`center`}
                >
                  {custom_gameplatform
                    ? custom_gameplatform === "all-platform"
                      ? `｜${i18n("all-platform")}`
                      : `｜${custom_gameplatform}`
                    : null}
                </Text>

                <Text
                  fontWeight={`700`}
                  fontSize={{
                    base: "20px",
                    md: "20px",
                  }}
                  display="flex"
                  alignItems={`center`}
                >
                  {subType ? (
                    <>｜{i18n(subType)}</>
                  ) : (
                    <>｜{i18n("all-platform")}</>
                  )}
                </Text>
              </Box>
            </Flex>
          </Box>
          <VerticalGameSwitcher />
        </>
      )}

      {subListSelector && (
        <Box mb="10px">
          <SubListSelector
            gameMainTypeFromProps={gameMainTypeFromProps}
            forceShowAll={
              !custom_gameplatform || custom_gameplatform == "all-platform"
            }
          />
        </Box>
      )}
      <CasinoGroupLink hiddenGameSearch={hiddenGameSearch} />

      <LoadingBlock size="50px" commonLoading={isLoading}>
        {displayedItems?.length === 0 && <NotFound />}
        <ChakraBox {...gameListFromBottom}>
          <Grid
            // templateColumns={{
            //   base: "repeat(3, minmax(0, 1fr))",
            //   md: "repeat(4, minmax(0, 1fr))",
            //   lg: "repeat(4, minmax(0, 1fr))",
            //   xl: "repeat(4, minmax(0, 1fr))",
            //   "2xl": "repeat(5, minmax(0, 1fr))",
            // }}
            templateColumns={
              ExclusiveLobbyStyle
                ? {
                    base: "repeat(2, minmax(0, 1fr))",
                    sm: "repeat(3, minmax(0, 1fr))",
                    md: "repeat(3, minmax(0, 1fr))",
                    lg: "repeat(3, minmax(0, 1fr))",
                    xl: "repeat(3, minmax(0, 1fr))",
                    "2xl": isMachineUser
                      ? "repeat(3, minmax(0, 1fr))"
                      : "repeat(4, minmax(0, 1fr))",
                  }
                : {
                    base: "repeat(2, minmax(0, 1fr))", // 0-480px: 3列
                    sm: "repeat(3, minmax(0, 1fr))", // 480px+: 3列
                    md: "repeat(4, minmax(0, 1fr))", // 768px+: 4列
                    lg: "repeat(4, minmax(0, 1fr))", // 992px+: 5列
                    xl: "repeat(5, minmax(0, 1fr))", // 1280px+: 5列
                    "2xl": "repeat(5, minmax(0, 1fr))", // 1536px+: 6列
                  }
            }
            sx={
              ExclusiveLobbyStyle
                ? {
                    "@media screen and (min-width: 1800px)": {
                      gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                    },
                    "@media screen and (min-width: 2200px)": {
                      gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
                    },
                    "@media screen and (min-width: 2560px)": {
                      gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
                    },
                    "@media screen and (min-width: 3840px)": {
                      gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
                    },
                  }
                : {
                    "@media screen and (min-width: 1800px)": {
                      gridTemplateColumns: "repeat(6, minmax(0, 1fr))", // 1800px+: 7列
                    },
                    "@media screen and (min-width: 2200px)": {
                      gridTemplateColumns: "repeat(7, minmax(0, 1fr))", // 2200px+: 7列
                    },
                    "@media screen and (min-width: 2560px)": {
                      gridTemplateColumns: "repeat(7, minmax(0, 1fr))", // 2K+: 8列
                    },
                    "@media screen and (min-width: 3840px)": {
                      gridTemplateColumns: "repeat(8, minmax(0, 1fr))", // 4K+: 10列
                    },
                  }
            }
            rowGap={{
              base: "0px",
              md: "0px",
            }}
            columnGap={{
              base: "0px",
              md: "0px",
            }}
            mt={{
              base: "10px",
              xl: gameMainTypeFromProps ? "0" : "10px", // xl以上時 如果gameMainTypeFromProps有值，則mt為-60px，否則為10px
            }}
          >
            {gameListSlice?.map((item, index) => {
              const gameName =
                item?.[
                  `gameName${languageTransfer({
                    lang: i18next.language,
                  })}`
                ];
              const gameNameEn = item?.gameNameEn;
              const platform = item?.game_platform;
              const gameSubType = item?.game_subtype;

              const gameCode = item?.gameCode;
              const gameType = item?.gameType;
              const gameUid = item?.gameUid;

              const isHighlighted = highlightUid == gameUid;

              return (
                <GridItem
                  key={gameUid || index}
                  id={`game-item-${gameUid}`}
                  colSpan={1}
                  rowSpan={1}
                  pb="2.5px"
                  borderRadius={"15px"}
                  mt="-10px"
                  position="relative"
                  onMouseEnter={() => {
                    onGameHover?.(item);
                    if (isHighlighted) setHighlightUid(null);
                  }}
                  onClick={() => {
                    sessionStorage.setItem("lastClickedGameUid", gameUid);
                  }}
                >
                  <Box position="relative">
                    <GameItem
                      gameProps={{
                        gameName,
                        platform,
                        gameCode,
                        gameType,
                        gameNameEn,
                        gameSubType,
                      }}
                      originProps={item}
                      itemClass={`!h-[130px]`}
                      itemIndex={index}
                      isNew={isNew}
                      isHot={isHot}
                      isHighlight={isHighlight}
                    />
                    {isHighlighted && (
                      <Flex
                        position="absolute"
                        top="0"
                        left="0"
                        right="0"
                        bottom="0"
                        bg="rgba(0,0,0,0.6)"
                        borderRadius="15px"
                        zIndex="10"
                        alignItems="center"
                        justifyContent="center"
                        pointerEvents="none"
                        border="2px solid"
                        borderColor="brand.400"
                      >
                        <Text
                          color="white"
                          fontSize="14px"
                          fontWeight="700"
                          textShadow="0px 0px 4px rgba(0,0,0,0.8)"
                        >
                          {i18n("lastPlayed") || "LAST VIEWED"}
                        </Text>
                      </Flex>
                    )}
                  </Box>
                </GridItem>
              );
            })}
          </Grid>
        </ChakraBox>
      </LoadingBlock>
      {/* </InfiniteScroll> */}
    </Box>
  );
};

export default GameSelector;
